import React from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Modal,
  Platform,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { useSubscription } from '../../hooks/useSubscription';
import { RootStackParamList } from '../../types/navigation';
import AccessibleText from '../common/AccessibleText';
import i18n from '../../i18n';
import { useFeatureFlag, isFeatureEnabled } from '../../hooks/useFeatureFlags';

interface PremiumFeatureProps {
  children: React.ReactNode;
}

/**
 * A component that wraps premium features and redirects to the paywall if the user is not premium
 */
const PremiumFeature: React.FC<PremiumFeatureProps> = ({ children }) => {
  const { isPremium } = useSubscription();
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const [modalVisible, setModalVisible] = React.useState(false);
  const { data: pricingFlag } = useFeatureFlag('pricing_subscription');
  const isPricingEnabled = isFeatureEnabled(
    pricingFlag,
    'pricing_subscription'
  );

  // If user is premium, just show the children
  if (isPremium) {
    return <>{children}</>;
  }

  // If pricing feature flag is enabled, show children without premium gate (hide paywall)
  if (isPricingEnabled) {
    return <>{children}</>;
  }

  const _handleUpgrade = () => {
    setModalVisible(false);
    navigation.navigate('PremiumPaywall');
  };

  const handleContinueFree = () => {
    setModalVisible(false);
  };

  return (
    <>
      <TouchableOpacity
        style={styles.container}
        onPress={() => setModalVisible(true)}
        accessibilityLabel={i18n.t('subscription.premiumRequired')}
        accessibilityRole="button"
        accessibilityHint={i18n.t('subscription.upgradeToAccess')}
      >
        <View style={styles.overlay}>
          <View style={styles.lockContainer}>
            <Ionicons name="lock-closed" size={30} color="#9475e1" />
          </View>
          <AccessibleText variant="h3" style={styles.premiumText}>
            {i18n.t('subscription.premiumRequired')}
          </AccessibleText>
          <AccessibleText variant="body" style={styles.upgradeText}>
            {i18n.t('subscription.upgradeToAccess')}
          </AccessibleText>
        </View>
      </TouchableOpacity>

      <Modal
        animationType="fade"
        transparent={true}
        visible={modalVisible}
        onRequestClose={() => setModalVisible(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.premiumIconContainer}>
              <Ionicons name="star" size={60} color="#9475e1" />
            </View>

            <AccessibleText variant="h2" style={styles.modalTitle}>
              {i18n.t('subscription.premiumFeature')}
            </AccessibleText>

            <AccessibleText variant="body" style={styles.modalDescription}>
              {i18n.t('subscription.upgradeToAccess')}
            </AccessibleText>

            {/* Upgrade button commented out for now */}
            {/* <TouchableOpacity 
              style={styles.upgradeButton}
              onPress={_handleUpgrade}
              accessibilityRole="button"
              accessibilityLabel={i18n.t('subscription.goToPremium')}
            >
              <AccessibleText variant="button" style={styles.upgradeButtonText}>
                {i18n.t('subscription.goToPremium')}
              </AccessibleText>
            </TouchableOpacity> */}

            <TouchableOpacity
              style={styles.cancelButton}
              onPress={handleContinueFree}
              accessibilityRole="button"
              accessibilityLabel={i18n.t('subscription.continueFree')}
            >
              <AccessibleText variant="button" style={styles.cancelButtonText}>
                {i18n.t('subscription.continueFree')}
              </AccessibleText>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    position: 'relative',
  },
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  lockContainer: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  premiumText: {
    color: '#FFFFFF',
    marginBottom: 8,
    textAlign: 'center',
  },
  upgradeText: {
    color: '#FFFFFF',
    opacity: 0.8,
    textAlign: 'center',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 24,
    width: '90%',
    maxWidth: 400,
    alignItems: 'center',
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.25,
        shadowRadius: 3.84,
      },
      android: {
        elevation: 5,
      },
    }),
  },
  premiumIconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: 'rgba(148, 117, 225, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  modalTitle: {
    marginBottom: 8,
    textAlign: 'center',
  },
  modalDescription: {
    marginBottom: 24,
    textAlign: 'center',
    color: '#6B7280',
  },
  upgradeButton: {
    backgroundColor: '#9475e1',
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 24,
    width: '100%',
    alignItems: 'center',
    marginBottom: 12,
  },
  upgradeButtonText: {
    color: '#FFFFFF',
  },
  cancelButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    width: '100%',
    alignItems: 'center',
  },
  cancelButtonText: {
    color: '#6B7280',
  },
});

export default PremiumFeature;
